import { html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { BaseComponent } from "../base-component/base-component";
import store from "../../store/store";
import emit from "../../common/events";
import { booleanConverter } from "../../common/converters";
import { captureAddToCartAnalytics } from "../../common/utils/analytics-util";
import { StoreState } from "../../store/store.interface";
import { ariaLabelWithNewTab } from '../../common/utils/a11y-util';

const CONTACT_US_URL = "https://mh.my.site.com/CSOM/s/";

@customElement('mhecomm-mhp-contact-us-to-buy')
export class MhpContactUsToBuy extends BaseComponent {

  /**
   * Once added, the product can be removed from cart if this property is true.
   */
  @property({ converter: booleanConverter })
  removeable = true;

  /**
   * If true, the user will be taken directly to the checkout page after clicking the add to cart button.
   */
  @property({ converter: booleanConverter })
  instantCheckout = false;

  /**
   * The ISBN of the product that you want the user to be able to add to their cart. This is a required propery.
   */
  @property()
  isbn = '';

  @property()
  isbn13 = '';

  /**
   * The business unit of the provided ISBN. This is a required propery.
   */
  @property()
  businessUnit = '';

  @property()
  copyRightYear = '';

  @property()
  description = '';

  @property()
  gradeLevel: string | undefined = '';

  /**
   * Which store this product is for. This parameter is optional.
   */
  @property()
  store?: string;

  /**
   * The net price of the product. This parameter needs passed in if showQuanity is set.
   */
  @property({ type: Number })
  netPrice?: number;

  /**
   * If true, the quantity field will be shown.
   */
  @property({ converter: booleanConverter })
  showPrice = false;

  /**
   * If true, the quantity field will be shown.
   */
  @property({ converter: booleanConverter })
  showQuantity = false;

  /**
   * If true, the total price will be calculated. This requires showQuantity to be true.
   */
  @property({ converter: booleanConverter })
  showTotal = false;

  /**
   * If true, the product restrictions link will be shown.
   * TODO: Until we get further requirements for the restrictions, we are turning them off
   */
  //@property({ converter: booleanConverter })
  showRestrictions = false;

  /**
   * If true, only remove link will be shown.
   */
  @property({ converter: booleanConverter })
  showRemoveLink = false;

  /**
   * Quantity value shall be captured using an Input field
   */
  @property({ type: Number })
  quantity = 1;

  /**
   * Instructor only attribute for user
   */
  @property({ converter: booleanConverter })
  instructorOnly = false;

  /**
   * The product title
   */
  @property()
  productTitle?: string;

  /**
   * The progarmId of the product
   */
  @property()
  programSourceId?: string;

  /**
   * The PDP type
   */
  @property()
  pdpType?: string;

  @property({ type: Number })
  listPrice?: number;

  /**
   * Offer pricing identifier
   */
  @property({ type: String })
  offerPricingId = '';

  @property()
  contributors: string | undefined;

  @property({ type: Number })
  edition: number | undefined;

  /**
   * Describes the ISBN purchase option's product type.
   */
  @property()
  poType?: string;

  renderComp() {
    return html`
      <small>
          To purchase this product, please 
          <a
            id=${`contact-us-to-buy-link-${this.isbn}`}
            href=${CONTACT_US_URL}
            data-automation-id=${`mhp-contact-us-to-buy-${this.isbn}`}
            target="_blank"
            class="fw-semibold mb-2"
            @click=${() => this.fireAnalytics()}
            aria-label="${ariaLabelWithNewTab('contact us to place your order')}"
          >
            contact us to to place your order
          </a>
          .
      </small>`;
  }

  private isEdoms(): boolean {
    return !!this.offerPricingId;
  }

  /**
   * Emits an event when the contact us here link is clicked.
   *
   *  * @returns {void}
   */
  private fireAnalytics(): void {
    emit({
      element: this,
      name: 'contact-us-to-buy',
      detail: this.dataLayerDetail(),
      dataLayer: true
    });
  }

  private dataLayerDetail() {
    const dataLayerProduct = window.adobeDataLayer?.getState?.()?.product;
    const dataLayerPurchaseOption =
      dataLayerProduct?.purchaseOptions?.find((option: any) =>
        (this.isEdoms() && this.offerPricingId === option.offerPricingId) ||
        (!this.isEdoms() && this.isbn === option.isbn || this.isbn === option.isbn13)) || {};
    const state = store.getState() as StoreState;
    return captureAddToCartAnalytics({
      state,
      store: this.store,
      isbn: this.isbn,
      businessUnit: this.businessUnit,
      copyrightYear: this.copyRightYear,
      instructorOnly: this.showRestrictions,
      isbn13: this.isbn13,
      netPrice: this.netPrice,
      listPrice: this.listPrice,
      offerPricingId: this.offerPricingId,
      isEdoms: this.isEdoms(),
      description: this.description,
      gradeLevel: this.gradeLevel,
      quantity: this.quantity,
      showQuantity: this.showQuantity,
      showTotal: this.showTotal,
      showPrice: this.showPrice,
      instantCheckout: this.instantCheckout,
      showRemoveLink: this.showRemoveLink,
      productTitle: this.productTitle,
      pdpType: this.pdpType,
      programSourceId: this.programSourceId,
      product: dataLayerProduct,
      purchaseOption: dataLayerPurchaseOption,
      contributors: this.contributors,
      edition: this.edition,
      poType: this.poType
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-mhp-contact-us-to-buy': MhpContactUsToBuy;
  }
}
